import { APP_QR_URL, isTry, setTry } from "./try";
import { type Lang, strings } from "./i18n";
import { signedDayWords, type ClientCopy, type HealthAnswer } from "./clientCopy";

function SuccessScreen({
  downloadLink,
  lang = "en",
  shop = "",
  clientCopy = null,
}: {
  downloadLink: string | null;
  lang?: Lang;
  /** the shop's name, which holds the signed waiver ("Ink & Iron Parlor has your signed waiver."); "" says "The shop" */
  shop?: string;
  /** 2026-10-08 — the client's own copy, for a shop in HIPAA mode whose state's rule hands the client one (clientCopy.ts).
   *  Absent for every other shop, whose done screen is the one it has always been (SuccessScreen.test.tsx pins it). */
  clientCopy?: ClientCopy | null;
}) {
  const T = strings(lang);
  // The owner who came from the app's "sign one yourself" door (try.ts) gets the way back; the
  // client at the counter gets the screen as it always was. Read once, then cleared, so a client
  // signing on the owner's phone later in the same tab is not shown the owner's door.
  const owner = isTry();
  if (owner) setTry(false);
  return (
    <div className="flex flex-col items-center justify-center">
      <h2 className="text-2xl font-bold mt-8 mb-8">{T.success}</h2>
      <div className="mb-4">
        <p className="text-lg">{T.submitted(shop)}</p>
      </div>
      {owner && (
        <div className="mb-6 text-center">
          <p className="text-lg mb-4">{T.ownerDone}</p>
          <a
            href={APP_QR_URL}
            className="inline-block rounded bg-black px-6 py-3 text-lg font-bold text-white no-underline"
          >
            {T.backToQr}
          </a>
        </div>
      )}
      {downloadLink && (
        <div className="mb-4">
          <p className="text-lg">
            <a
              href={downloadLink}
              download
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {T.download}
            </a>
          </p>
        </div>
      )}
      {clientCopy && <YourCopy copy={clientCopy} />}
    </div>
  );
}

/** 2026-10-08 — THE CLIENT'S OWN COPY OF WHAT THEY SIGNED (clientCopy.ts says what it draws and why), in the language they
 *  signed in whatever the switch says now: it is a copy of what they signed. The line about the shop's printed copy and the
 *  button sit OUTSIDE the copy, so the paper carries the copy and nothing else (index.css, the print rules). Nothing in it
 *  is a link: what the client keeps is what is on this screen, and it is gone with the next waiver. */
function YourCopy({ copy }: { copy: ClientCopy }) {
  const T = strings(copy.lang);
  // the answer as the signed record prints it: "Yes" with what a yes said, "No", or not answered (the one optional question)
  const answer = (a: HealthAnswer): string =>
    a.answer === "yes" ? (a.details ? `${T.yes} (${a.details})` : T.yes) : a.answer === "no" ? T.no : T.copyNotAnswered;
  return (
    <>
      <div className="mb-6 text-center" lang={copy.lang}>
        <p className="text-lg mb-4">{T.copyShopPrints(copy.shop)}</p>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-block rounded bg-black px-6 py-3 text-lg font-bold text-white"
        >
          {T.copyPrint}
        </button>
      </div>
      <section
        className="client-copy mb-8 w-full max-w-2xl rounded border border-gray-300 p-4 text-left"
        aria-labelledby="client-copy-heading"
        lang={copy.lang}
      >
        <h3 id="client-copy-heading" className="text-xl font-bold">
          {T.copyHeading(copy.shop)}
        </h3>
        <p className="mt-1 text-base">{copy.title}</p>
        <p className="mt-3 text-base">
          <span className="font-semibold">{T.name}:</span> {copy.name}
        </p>
        <p className="text-base">
          <span className="font-semibold">{T.signedDate}:</span> {signedDayWords(copy.date, copy.lang)}
        </p>
        <h4 className="mt-4 mb-2 text-lg font-semibold">{T.acknowledgment}</h4>
        <ul className="list-disc pl-5 text-base">
          {copy.signed.map((line, index) =>
            line.kind === "sheet" ? (
              <li key={index} className="mb-2 list-none -ml-5" data-copy-line="sheet">
                <p className="font-medium">{line.text}</p>
                {line.intro ? <p className="mb-1 text-sm">{line.intro}</p> : null}
                {line.sections.map((section) => (
                  <div key={section.title} className="mb-1 text-sm">
                    <p className="font-semibold">{section.title}</p>
                    {section.lead ? <p>{section.lead}</p> : null}
                    <ul className="list-disc pl-5">
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    {section.tail ? <p>{section.tail}</p> : null}
                  </div>
                ))}
              </li>
            ) : line.kind === "given" ? (
              <li key={index} className="mb-2 list-none -ml-5 border-l-2 border-gray-300 pl-3" data-copy-line="given">
                {line.text}
              </li>
            ) : (
              <li key={index} className="mb-1" lang={line.lang} data-copy-line="agreed">
                {line.text}
              </li>
            )
          )}
        </ul>
        {copy.release && (
          <div data-copy-release="signed">
            <h4 className="mt-4 mb-2 text-lg font-semibold">{copy.release.heading}</h4>
            <ul className="list-disc pl-5 text-base">
              {copy.release.lines.map((line) => (
                <li key={line} className="mb-1">
                  {line}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-base font-semibold">{copy.release.box}</p>
          </div>
        )}
        {copy.releaseDeclined && (
          <p className="mt-4 text-base" data-copy-release="declined">
            {T.copyReleaseDeclined}
          </p>
        )}
        <h4 className="mt-4 mb-2 text-lg font-semibold">{T.questionnaire}</h4>
        <ul className="text-base">
          {copy.health.map((a, index) => (
            <li key={`q${index}`} className="mb-2" data-copy-answer={a.answer || "none"}>
              {a.question} <span className="font-semibold">{answer(a)}</span>
              {/* TW-0412 — the shop's line the page showed under this answer to one of its own questions, as written */}
              {a.note ? (
                <span className="block text-gray-700" data-copy-note="">
                  {a.note}
                </span>
              ) : null}
            </li>
          ))}
          {copy.notes.map((note, index) => (
            <li key={`n${index}`} className="mb-2">
              {note.label} <span className="font-semibold">{note.text || T.copyNone}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

export default SuccessScreen;
